import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, BookOpen, Repeat, MessageCircle, Headphones,
    Map, TrendingUp, LogOut, Menu, X, Languages, Command as CommandIcon,
    Briefcase, Settings, CreditCard,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardProvider, useDashboard } from "@/contexts/DashboardContext";
import CommandPalette from "@/components/CommandPalette";
import LexiaVoiceWidget from "@/components/LexiaVoiceWidget";
import SiteFooter from "@/components/SiteFooter";
import { LexiaVoiceProvider } from "@/contexts/LexiaVoiceContext";
import { useBadgeToaster } from "@/hooks/use-badge-toaster";

const NAV = [
    { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/lesson", label: "Leçon du jour", icon: BookOpen, testid: "nav-lesson" },
    { to: "/reviews", label: "Révisions", icon: Repeat, testid: "nav-reviews", badge: "reviews_due" },
    { to: "/conversation", label: "Conversation", icon: MessageCircle, testid: "nav-conversation" },
    { to: "/translate", label: "Traducteur", icon: Languages, testid: "nav-translate" },
    { to: "/podcasts", label: "Podcasts", icon: Headphones, testid: "nav-podcasts" },
    { to: "/parcours", label: "Parcours pro", icon: Briefcase, testid: "nav-parcours" },
    { to: "/progress", label: "Progression", icon: TrendingUp, testid: "nav-progress" },
    { to: "/abonnement", label: "Abonnement", icon: CreditCard, testid: "nav-subscription" },
    { to: "/settings/profile", label: "Profil", icon: Settings, testid: "nav-settings" },
];

export default function AppShell() {
    return (
        <DashboardProvider>
            <LexiaVoiceProvider>
                <AppShellInner />
            </LexiaVoiceProvider>
        </DashboardProvider>
    );
}

function AppShellInner() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    // Phase 9: background toast for newly earned badges.
    useBadgeToaster();

    useEffect(() => {
        function onToggle() {
            setPaletteOpen((v) => !v);
        }
        window.addEventListener("lexi:palette:toggle", onToggle);
        return () => window.removeEventListener("lexi:palette:toggle", onToggle);
    }, []);

    const onLogout = () => {
        // Navigate FIRST so we leave the protected subtree while still authenticated,
        // then clear the session. Otherwise ProtectedRoute redirects to /login before
        // the navigation resolves.
        navigate("/", { replace: true });
        setTimeout(() => logout(), 0);
    };

    return (
        <div className="min-h-screen bg-paper flex" data-testid="app-shell">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-lexiBorder bg-white" data-testid="sidebar">
                <SidebarBody onLogout={onLogout} user={user} onOpenPalette={() => setPaletteOpen(true)} />
            </aside>

            {/* Mobile top bar + drawer */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-40 border-b border-lexiBorder bg-white flex items-center justify-between px-4 h-14">
                <Link to="/dashboard" className="font-serif text-lg" data-testid="mobile-brand">
                    Lexia School
                </Link>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPaletteOpen(true)}
                        aria-label="Ouvrir les commandes rapides"
                        data-testid="open-palette-mobile"
                        className="p-2 rounded-full hover:bg-paperAlt"
                    >
                        <CommandIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setOpen(true)}
                        aria-label="Ouvrir le menu"
                        data-testid="open-mobile-menu"
                        className="p-2 rounded-full hover:bg-paperAlt"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </div>
            {open && (
                <div className="lg:hidden fixed inset-0 z-50 bg-black/30" onClick={() => setOpen(false)}>
                    <aside
                        onClick={(e) => e.stopPropagation()}
                        className="w-72 h-full bg-white flex flex-col border-r border-lexiBorder"
                        data-testid="mobile-sidebar"
                    >
                        <div className="flex items-center justify-between p-4 border-b border-lexiBorder">
                            <span className="font-serif text-lg">
                                Lexia School
                            </span>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="close-mobile-menu"
                                className="p-2 rounded-full hover:bg-paperAlt"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <SidebarBody onLogout={onLogout} user={user} onNavigate={() => setOpen(false)} onOpenPalette={() => { setOpen(false); setPaletteOpen(true); }} />
                    </aside>
                </div>
            )}

            <main className="flex-1 min-w-0 pt-14 lg:pt-0 flex flex-col">
                <div className="flex-1">
                    <Outlet />
                </div>
                <SiteFooter tone="paper" compact />
            </main>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
            <LexiaVoiceWidget />
        </div>
    );
}

function SidebarBody({ onLogout, user, onNavigate, onOpenPalette }) {
    const { data } = useDashboard();
    const badges = { reviews_due: data?.stats?.reviews_due || 0 };
    return (
        <>
            <div className="p-6 border-b border-lexiBorder hidden lg:block">
                <span className="font-serif text-xl">
                    Lexia School
                </span>
            </div>
            <div className="px-3 pt-3">
                <button
                    onClick={onOpenPalette}
                    data-testid="open-palette-btn"
                    className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-lexiBorder bg-paperAlt hover:bg-paper text-sm text-inkSoft transition-colors"
                >
                    <CommandIcon className="w-3.5 h-3.5" />
                    <span className="flex-1 text-left">Commandes rapides…</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-lexiBorder">
                        ⌘K
                    </span>
                </button>
            </div>
            <nav className="flex-1 px-3 py-3 space-y-1">
                {NAV.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={onNavigate}
                        data-testid={n.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                                isActive
                                    ? "bg-ink text-white"
                                    : "text-inkSoft hover:text-ink hover:bg-paperAlt"
                            }`
                        }
                    >
                        <n.icon className="w-4 h-4" strokeWidth={1.75} />
                        <span className="flex-1">{n.label}</span>
                        {n.badge && badges[n.badge] > 0 && (
                            <span
                                className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-terracotta text-white"
                                data-testid={`badge-${n.badge}`}
                            >
                                {badges[n.badge]}
                            </span>
                        )}
                    </NavLink>
                ))}
            </nav>
            <div className="p-4 border-t border-lexiBorder">
                <div className="text-xs text-inkSoft mb-2">Connecté en tant que</div>
                <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                    {user?.first_name}
                </div>
                <div className="text-xs text-inkSoft truncate" data-testid="sidebar-user-email">
                    {user?.email}
                </div>
                <button
                    onClick={onLogout}
                    data-testid="logout-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-paperAlt hover:bg-paper border border-lexiBorder rounded-full py-2 text-sm transition-colors"
                >
                    <LogOut className="w-3.5 h-3.5" />
                    Déconnexion
                </button>
            </div>
        </>
    );
}
